/**
 * Round 20 H58 -- a person, found by name: their seats and what they
 * signed.
 *
 * Searching a signatory's name answered "No matches", so a signer
 * could not be traced. A name the product knows only from signatures
 * (somebody who signed and has never signed in) is said as that, not
 * dressed as an account.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { formatDateTime } from "shared/time/formatDate";
import { roleLabel } from "shared/vocab/workflowState";

interface PersonSignature {
  kind: string;
  leg: string;
  subject: string | null;
  at: string | null;
  link: string;
}

interface Person {
  name: string;
  has_account: boolean;
  username: string | null;
  seats: string[];
  signatures: PersonSignature[];
}

export function PeopleView(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const name = params.get("name") ?? "";
  const person = useQuery({
    queryKey: ["people", name],
    queryFn: () => api.get<Person>(`/v1/people?name=${encodeURIComponent(name)}`),
    enabled: name.length > 0,
    retry: false,
  });
  const notFound = person.error instanceof ApiError && person.error.status === 404;
  const p = person.data;
  return (
    <section data-testid="people-view" className="flex max-w-3xl flex-col gap-4">
      <header>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="dual-signoff"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {name || t("people.heading")}
        </h1>
      </header>
      {person.isLoading && (
        <p className="text-sm text-slate-500">{t("people.loading")}</p>
      )}
      {notFound && (
        <p data-testid="people-not-found" className="text-sm text-slate-600">
          {t("people.notFound", { name })}
        </p>
      )}
      {person.isError && !notFound && (
        <p className="text-sm text-rag-red">{t("people.error")}</p>
      )}
      {p && (
        <>
          <p data-testid="people-account" className="text-sm text-slate-700">
            {p.has_account
              ? p.seats.length
                ? t("people.seats", {
                    username: p.username ?? "",
                    seats: p.seats.map((s) => roleLabel(s)).join(", "),
                  })
                : t("people.noSeats", { username: p.username ?? "" })
              : t("people.noAccount")}
          </p>
          <div>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("people.signedHeading")}
            </h2>
            {p.signatures.length === 0 ? (
              <p className="text-sm text-slate-500">{t("people.signedNone")}</p>
            ) : (
              <ul
                data-testid="people-signatures"
                className="flex flex-col gap-1 text-sm"
              >
                {p.signatures.map((s, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="tabular-nums text-slate-500">
                      {s.at ? formatDateTime(s.at, i18n.language) : "—"}
                    </span>
                    <span className="text-slate-700">
                      {t(`people.kind.${s.kind}`, { defaultValue: s.kind })} ·{" "}
                      {s.leg === "challenge" ? t("people.challenge") : roleLabel(s.leg)}
                    </span>
                    <Link to={s.link} className="text-brand-brassDark hover:underline">
                      {s.subject ?? t("people.openRecord")}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}
